import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { createSelector } from 'reselect';
import { Store } from '@/core/flux.service';
import { isHierarchySearch, isHierarchySearchOutput } from '@/itemFinder/itemFinder.utilities';
import { columnRuleOutputToColumnRuleInput } from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import {
  DEFAULT_COLUMN_DEFINITION_INPUTS,
  DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  buildCellEvaluationResultsCache,
  columnDefinitionOutputToColumnDefinitionInput,
  generateTableName,
  getColumnDisplayName,
  getColumnsNotIncludedInAgGrid,
  tableDefinitionOutputToTableDefinitionInput,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { BatchActionEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { GridApi } from 'ag-grid-community';
import type { PersistenceLevel } from '@/core/flux.service';
import type { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type {
  ColumnDefinition,
  MaterializedTableOutput,
  ModelToSelect,
  ProcessedMaterializedTable,
  RowLoadProgress,
  StoredTableSearchFailures,
  TableJoinData,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { HierarchyOutputV1 } from '@seeqdev/api/model/HierarchyOutputV1';
import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { SearchTypeEnum } from '@seeqdev/api/model/PropertySearchV1';
import type { RowRulesResultV1 } from '@seeqdev/api/model/RowRulesResultV1';
import type { RulesResultV1 } from '@seeqdev/api/model/RulesResultV1';
import type { TableDefinitionInputV1, TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';
import type { TemplateTableApplicationsOutputListV1 } from '@seeqdev/api/model/TemplateTableApplicationsOutputListV1';

export interface BaseTableDefinitionStoreState {
  id: string;
  subscriberId: string;
  name: string;
  searchTypeAI?: SearchTypeEnum;
  description: string;
  scopedTo?: string;
  batchAction: BatchActionEnum;
  tableType: TableTypeEnum;
  columns: ColumnDefinition[];
  hasUnsupportedSubscription?: boolean;
  shouldSearchForItems: boolean;
  tableDefinitionInput: any; // any because of Baobab's `monkey()`;
  tableDefinitionOutput?: TableDefinitionOutputV1;
  materializedTable?: ProcessedMaterializedTable;
  tableJoinData: any; // any because of Baobab's `monkey()`;
  hasHierarchySearch: any; // any because of Baobab's `monkey()`;
  doTableReload: boolean;
  itemColumnsThatCouldBeUsedToAutoCreateTextColumns: any; // any because of Baobab's `monkey()`;
  pendingTableIds: Record<string, boolean>;
  createdItemFinderId?: string;
  allowHiddenColumnsAsInputs: boolean;
  agGridApi?: GridApi;
  rowLoadProgress?: RowLoadProgress;
  rowDataIsLoading: boolean;
  /** True while an existing table definition is being fetched after edit/open. */
  isLoadingDefinition: boolean;
  selectedColumns: string[];
  showConfigureColumn: boolean;
  scrollToColumnId: string;
  rowItemIdsInsertedInGroupLevel: string[];
  showCreateTemplate: boolean;
  isApplyingTemplate: boolean;
  templateInputToSelect?: ModelToSelect;
  columnIdToBeUsedAsTemplateInput?: string;
  templateColumns?: TemplateTableApplicationsOutputListV1;
  isEditingTemplate: boolean;
  editTemplateRemovedOutputByColumnId: Record<string, DerivedFeatureV1>;
  hierarchiesUsingThisColumnAsGroup: HierarchyOutputV1[];
  columnToDelete?: ColumnDefinition;
  showRetireTemplateModal: boolean;
  storedTableFailures: StoredTableSearchFailures[];
  storedTables: ItemSearchPreviewV1[];
  /** Keyed by (normalized) templateContainerId — see `InputFeatureColumnIdsByTemplateContainer`. Can be cleared to
   * undefined; consumers must treat a missing value as "no input features". */
  inputFeatureColumnIds?: Record<string, string[]>;
  /** Detailed rule evaluation results keyed by `${rowId}|${columnId}`. */
  cellEvaluationResultsCache: Record<string, RulesResultV1[]>;
}

/**
 * Base class store for the table definition
 * The table definition state is stored here
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class BaseTableDefinitionStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  abstract defaultName: string;
  setupState(defaultType: TableTypeEnum) {
    const initialState: BaseTableDefinitionStoreState = {
      id: '',
      subscriberId: '',
      name: '',
      searchTypeAI: undefined,
      description: '',
      scopedTo: undefined,
      batchAction: BatchActionEnum.UPDATEEXISTINGINSERTNEWCLEANUP,
      tableType: defaultType,
      columns: [],
      hasUnsupportedSubscription: undefined,
      shouldSearchForItems: false,
      tableDefinitionInput: this.monkey(
        ['subscriberId'],
        ['name'],
        ['description'],
        ['scopedTo'],
        ['batchAction'],
        ['tableType'],
        ['columns'],
        function (
          subscriberId: string,
          name: string,
          description: string,
          scopedTo: string | undefined,
          batchAction: BatchActionEnum,
          tableType: TableTypeEnum,
          columnDefinitions: ColumnDefinition[],
        ): TableDefinitionInputV1 {
          const columnDefinitionInputs =
            columnDefinitions.length === 0
              ? DEFAULT_COLUMN_DEFINITION_INPUTS
              : columnDefinitions.map((column) =>
                  columnDefinitionOutputToColumnDefinitionInput(column, columnDefinitions),
                );

          return {
            subscriptionId: subscriberId,
            name,
            description,
            scopedTo,
            batchAction,
            tableType,
            columnDefinitions: columnDefinitionInputs,
          };
        },
      ),
      tableDefinitionOutput: undefined,
      materializedTable: undefined,
      tableJoinData: this.monkey(
        ['tableDefinitionOutput'],
        (tableDefinitionOutput: TableDefinitionOutputV1 | undefined) => {
          if (!tableDefinitionOutput?.itemFinder) {
            return undefined;
          }
          const hierarchiesUsed = tableDefinitionOutput?.itemFinder?.searches
            .filter(isHierarchySearchOutput)
            .map((search) => {
              return {
                hierarchy: search.hierarchy as HierarchyOutputV1,
                tableDefinition: search.hierarchy.tableDefinitionOutput as TableDefinitionOutputV1,
              };
            });

          return hierarchiesUsed
            .map(({ hierarchy, tableDefinition }): TableJoinData | undefined => {
              const aliasColumnId = hierarchy.aliasColumnId;
              let aliasColumn: ColumnDefinitionOutputV1 | undefined;
              if (aliasColumnId) {
                aliasColumn = tableDefinition.columnDefinitions.find((col) => col.id === aliasColumnId);
              }
              const columnIdsToInclude = aliasColumnId ? [aliasColumnId] : undefined;
              return columnIdsToInclude
                ? {
                    tableJoinInput: {
                      tableDefinitionId: hierarchy.tableDefinitionOutput.id,
                      columnIdsToInclude,
                    },
                    aliasColumnId,
                    aliasColumnName: aliasColumn?.columnName,
                  }
                : undefined;
            })
            .filter((join) => join !== undefined);
        },
      ),
      hasHierarchySearch: this.monkey(
        ['tableDefinitionOutput'],
        (tableDefinitionOutput: TableDefinitionOutputV1 | undefined) => {
          if (tableDefinitionOutput?.itemFinder) {
            return tableDefinitionOutput.itemFinder.searches.some((search) => isHierarchySearch(search));
          }
          return false;
        },
      ),
      doTableReload: false,
      itemColumnsThatCouldBeUsedToAutoCreateTextColumns: this.monkey(
        ['columns'],
        ['tableType'],
        (columns: ColumnDefinition[], tableType: TableTypeEnum) => {
          return (
            columns
              .map((column, index) => ({ ...column, columnIndex: index + 1 }))
              .filter((column) => column.columnType === ColumnTypeEnum.UUID && column.displayProperty)
              .filter((column) => !getColumnsNotIncludedInAgGrid(tableType, columns).includes(column.columnName))
              // filter out an item column if there is a text column whose first rule is item property and has the same property
              // as the item column is currently displaying
              .filter((itemColumnWithProperty) => {
                const textColumnWithItemPropertyRule = columns.find((col) => {
                  const getItemPropertyRule =
                    col.columnType === ColumnTypeEnum.TEXT &&
                    col.rules.length > 0 &&
                    col.rules[0].rule === SeeqNames.MaterializedTables.Rules.GetItemProperty
                      ? columnRuleOutputToColumnRuleInput(
                          col.rules[0],
                          columns.filter((columnDef) => columnDef.columnName !== col.columnName),
                        )
                      : undefined;
                  const isPropertyMatch =
                    getItemPropertyRule?.getItemProperty?.propertyName === itemColumnWithProperty.displayProperty;
                  const inputColumnOfRuleIsItemColumn =
                    getItemPropertyRule?.getItemProperty?.columnIndex === itemColumnWithProperty.columnIndex;
                  return isPropertyMatch && inputColumnOfRuleIsItemColumn ? col : undefined;
                });
                return !textColumnWithItemPropertyRule;
              })
              .map((column) => ({
                columnName: column.columnName,
                displayProperty: column.displayProperty,
                displayName: column.displayName,
              })) as Required<Pick<ColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>>[]
          );
        },
      ),
      pendingTableIds: {},
      createdItemFinderId: undefined,
      allowHiddenColumnsAsInputs: false,
      agGridApi: undefined,
      rowLoadProgress: undefined,
      rowDataIsLoading: false,
      isLoadingDefinition: false,
      selectedColumns: [],
      showConfigureColumn: false,
      scrollToColumnId: '',
      rowItemIdsInsertedInGroupLevel: [],
      showCreateTemplate: false,
      isApplyingTemplate: false,
      templateInputToSelect: undefined,
      columnIdToBeUsedAsTemplateInput: undefined,
      templateColumns: undefined,
      isEditingTemplate: false,
      editTemplateRemovedOutputByColumnId: {} as Record<string, DerivedFeatureV1>,
      hierarchiesUsingThisColumnAsGroup: [],
      columnToDelete: undefined,
      showRetireTemplateModal: false,
      storedTableFailures: [] as StoredTableSearchFailures[],
      storedTables: [] as ItemSearchPreviewV1[],
      inputFeatureColumnIds: {} as Record<string, string[]>,
      cellEvaluationResultsCache: {} as Record<string, RulesResultV1[]>,
    };
    this.state = this.immutable(initialState);
  }

  getName: (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => string =
    createSelector(
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.tableDefinitions,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.id,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.currentName,
      (tableDefinitions: ItemSearchPreviewV1[], id: string, currentName?: string) => {
        return generateTableName(id, tableDefinitions, this.defaultName, currentName);
      },
    );

  abstract getRulesNotAllowed(): ColumnRule[];

  abstract getMaterializedTableCancellationGroup(): string;
  abstract getTableDefinitionCancellationGroup(): string;

  get rowItemIdsInsertedInGroupLevel(): string[] {
    return this.state.get('rowItemIdsInsertedInGroupLevel');
  }

  get hierarchiesUsingThisColumnAsGroup(): HierarchyOutputV1[] {
    return this.state.get('hierarchiesUsingThisColumnAsGroup');
  }

  get columnToDelete(): ColumnDefinition | undefined {
    return this.state.get('columnToDelete');
  }

  get tableJoinData(): TableJoinData[] | undefined {
    return this.state.get('tableJoinData');
  }

  get id(): string {
    return this.state.get('id');
  }

  get shouldSearchForItems(): boolean {
    return this.state.get('shouldSearchForItems');
  }

  get pendingTableIds(): Record<string, boolean> {
    return this.state.get('pendingTableIds');
  }

  get subscriberId(): string {
    return this.state.get('subscriberId');
  }

  get createdItemFinderId(): string {
    return this.state.get('createdItemFinderId');
  }

  abstract get name(): string;

  get tableType(): TableTypeEnum {
    return this.state.get('tableType');
  }

  get searchTypeAI(): SearchTypeEnum | undefined {
    return this.state.get('searchTypeAI');
  }

  get description(): string {
    return this.state.get('description');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get batchAction(): BatchActionEnum {
    return this.state.get('batchAction');
  }

  get columns(): ColumnDefinition[] {
    return this.state.get('columns');
  }

  get tableDefinitionInput(): TableDefinitionInputV1 {
    return this.state.get('tableDefinitionInput');
  }

  get tableDefinitionOutput(): TableDefinitionOutputV1 {
    return this.state.get('tableDefinitionOutput');
  }

  get materializedTable(): ProcessedMaterializedTable | undefined {
    return this.state.get('materializedTable');
  }

  get cellEvaluationResultsCache(): Record<string, RulesResultV1[]> {
    return this.state.get('cellEvaluationResultsCache');
  }

  get itemColumnsThatCouldBeUsedToAutoCreateTextColumns(): Required<
    Pick<ColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>
  >[] {
    return this.state.get('itemColumnsThatCouldBeUsedToAutoCreateTextColumns');
  }

  get hasHierarchySearch(): boolean {
    return this.state.get('hasHierarchySearch');
  }

  get hasUnsupportedSubscription(): boolean | undefined {
    return this.state.get('hasUnsupportedSubscription');
  }

  get doTableReload(): boolean {
    return this.state.get('doTableReload');
  }

  get allowHiddenColumnsAsInputs(): boolean {
    return this.state.get('allowHiddenColumnsAsInputs');
  }

  get agGridApi(): GridApi | undefined {
    return this.state.get('agGridApi');
  }

  get selectedColumns(): string[] {
    return this.state.get('selectedColumns');
  }

  get showConfigureColumn(): boolean {
    return this.state.get('showConfigureColumn');
  }

  get scrollToColumnId(): string {
    return this.state.get('scrollToColumnId');
  }

  get rowLoadProgress(): RowLoadProgress | undefined {
    return this.state.get('rowLoadProgress');
  }

  get rowDataIsLoading(): boolean {
    return this.state.get('rowDataIsLoading');
  }

  get isLoadingDefinition(): boolean {
    return this.state.get('isLoadingDefinition');
  }

  get showCreateTemplate(): boolean {
    return this.state.get('showCreateTemplate');
  }

  get isApplyingTemplate(): boolean {
    return this.state.get('isApplyingTemplate');
  }

  get templateInputToSelect(): ModelToSelect | undefined {
    return this.state.get('templateInputToSelect');
  }

  get columnIdToBeUsedAsTemplateInput(): string | undefined {
    return this.state.get('columnIdToBeUsedAsTemplateInput');
  }

  get templateColumns(): TemplateTableApplicationsOutputListV1 {
    return this.state.get('templateColumns');
  }

  get inputFeatureColumnIds(): Record<string, string[]> | undefined {
    return this.state.get('inputFeatureColumnIds');
  }

  get isEditingTemplate(): boolean {
    return this.state.get('isEditingTemplate');
  }

  get showRetireTemplateModal(): boolean {
    return this.state.get('showRetireTemplateModal');
  }

  get editTemplateRemovedOutputByColumnId(): Record<string, DerivedFeatureV1> {
    return this.state.get('editTemplateRemovedOutputByColumnId');
  }

  get storedTableFailures(): StoredTableSearchFailures[] {
    return this.state.get('storedTableFailures');
  }

  get storedTables(): ItemSearchPreviewV1[] {
    return this.state.get('storedTables');
  }

  protected readonly handlers = {};

  setSubscriberId = ({ subscriberId }: { subscriberId: string | undefined }) => {
    this.state.set('subscriberId', subscriberId);
  };

  setShouldSearchForItems = ({ shouldSearchForItems }: { shouldSearchForItems: boolean }) => {
    this.state.set('shouldSearchForItems', shouldSearchForItems);
  };

  setName = ({ name }: { name: string }) => {
    this.state.set('name', name);
  };

  setSearchTypeAI = ({ searchTypeAI }: { searchTypeAI: SearchTypeEnum }) => {
    this.state.set('searchTypeAI', searchTypeAI);
  };

  setDescription = ({ description }: { description: string }) => {
    this.state.set('description', description);
  };

  setBatchAction = ({ batchAction }: { batchAction: BatchActionEnum }) => {
    this.state.set('batchAction', batchAction);
  };

  setDoTableReload = ({ doTableReload }: { doTableReload: boolean }) => {
    this.state.set('doTableReload', doTableReload);
  };

  setTableDefinition = ({ tableDefinition }: { tableDefinition: TableDefinitionOutputV1 }) => {
    this.state.set('tableDefinitionOutput', tableDefinition);

    this.state.set('id', tableDefinition.id);
    this.state.set('name', tableDefinition.name);

    const hasUnsupportedSubscription = tableDefinition.subscription?.type === SeeqNames.Types.ConditionMonitor;
    this.state.set('hasUnsupportedSubscription', hasUnsupportedSubscription);
    if (hasUnsupportedSubscription) {
      return;
    }

    const tableDefinitionInput = tableDefinitionOutputToTableDefinitionInput(tableDefinition, this.scopedTo);
    this.state.set('subscriberId', tableDefinitionInput.subscriptionId);
    this.state.set('description', tableDefinitionInput.description);
    this.state.set('scopedTo', tableDefinitionInput.scopedTo);
    this.state.set('batchAction', tableDefinitionInput.batchAction);
    this.state.set('tableType', tableDefinitionInput.tableType);
    const currentColumns: ColumnDefinition[] = this.state.get('columns');
    const newColumnDefinitions = tableDefinition.columnDefinitions;
    const updatedColumns: ColumnDefinition[] = newColumnDefinitions.map((newColumn) => {
      const existingColumn = currentColumns.find((column) => column.columnName === newColumn.columnName);
      const displayName = getColumnDisplayName(newColumn.columnName, this.tableDefinitionOutput.uniqueIDColumnName);
      const displayProperty = newColumn.columnType === ColumnTypeEnum.UUID ? newColumn?.displayProperty : null;
      const isUUIDColumn = newColumn.columnType === ColumnTypeEnum.UUID;
      const hideColumnAsInput = newColumn.rules.some(
        (rule) => rule.rule === SeeqNames.MaterializedTables.Rules.SetItemProperty,
      );
      const additionalProperties = isUUIDColumn
        ? (existingColumn?.additionalProperties ?? DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS)
        : undefined;
      const isHiddenFromDropdown = hideColumnAsInput || existingColumn?.isHiddenFromDropdown;
      return {
        displayName,
        ...(displayProperty !== null && { displayProperty }),
        additionalProperties,
        isHiddenFromDropdown,
        ...newColumn,
      };
    });
    this.state.set('columns', updatedColumns);
    if (!_.isEqual(currentColumns, updatedColumns)) {
      this.state.set('doTableReload', true);
    }
  };

  setItemFinder = ({ itemFinder }: { itemFinder: ItemFinderOutputV1 | undefined }) => {
    const currentOutput: TableDefinitionOutputV1 | undefined = this.state.get('tableDefinitionOutput');
    if (currentOutput) {
      this.state.set('tableDefinitionOutput', { ...currentOutput, itemFinder });
    }
  };

  setMaterializedTable = ({ materializedTable }: { materializedTable: MaterializedTableOutput | undefined }) => {
    const persistedColumns: ColumnDefinition[] = this.state.get('columns');
    this.state.set('materializedTable', processMaterializedTable(materializedTable, persistedColumns));
  };

  setEvaluationResults = ({ evaluationResults }: { evaluationResults: { [key: string]: RowRulesResultV1[] } }) => {
    // Only update the cache. The cell renderer subscribes to it via `useFluxPath` and re-renders reactively, so the
    // rule evaluation details icon shows/hides on its own — no imperative `agGridApi.refreshCells` needed (that was
    // unreliable under reactiveCustomComponents and could orphan a duplicate cell when it coincided with a reload).
    this.state.set('cellEvaluationResultsCache', buildCellEvaluationResultsCache(evaluationResults));
  };

  mergeEvaluationResults = ({ evaluationResults }: { evaluationResults: { [key: string]: RowRulesResultV1[] } }) => {
    const existing = this.state.get('cellEvaluationResultsCache');
    const incoming = buildCellEvaluationResultsCache(evaluationResults);
    this.state.set('cellEvaluationResultsCache', { ...existing, ...incoming });
  };

  reset = () => {
    this.initialize();
  };

  addPendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.set(['pendingTableIds', pendingTableId], true);
  };

  removePendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.unset(['pendingTableIds', pendingTableId]);
  };

  setCreatedItemFinderId = ({ createdItemFinderId }: { createdItemFinderId: string | undefined }) => {
    this.state.set('createdItemFinderId', createdItemFinderId);
  };

  setAllowHiddenColumnsAsInputs = ({ allowHiddenColumnsAsInputs }: { allowHiddenColumnsAsInputs: boolean }) => {
    this.state.set('allowHiddenColumnsAsInputs', allowHiddenColumnsAsInputs);
  };

  setAgGridApi = ({ agGridApi }: { agGridApi: GridApi }) => {
    this.state.set('agGridApi', agGridApi);
  };

  setRowDataIsLoading = ({ rowDataIsLoading }: { rowDataIsLoading: boolean }) => {
    this.state.set('rowDataIsLoading', rowDataIsLoading);
  };

  setIsLoadingDefinition = ({ isLoadingDefinition }: { isLoadingDefinition: boolean }) => {
    this.state.set('isLoadingDefinition', isLoadingDefinition);
  };

  requestSetProgress = (payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  }) => {
    if (payload.id === this.id) {
      this.state.set('rowLoadProgress', {
        progress: payload.progress ?? 0,
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
      });
    }
  };

  setSelectedColumns = ({ selectedColumns }: { selectedColumns: string[] }) => {
    this.state.set('selectedColumns', selectedColumns);
  };

  setShowConfigureColumn = ({ show }: { show: boolean }) => {
    this.state.set('showConfigureColumn', show);
  };

  setScrollToColumnId = ({ scrollToColumnId }: { scrollToColumnId: string }) => {
    this.state.set('scrollToColumnId', scrollToColumnId);
  };

  setIsApplyingTemplate = ({ isApplyingTemplate }: { isApplyingTemplate: boolean }) => {
    this.state.set('isApplyingTemplate', isApplyingTemplate);
  };

  setTemplateInputToSelect = ({ templateInputToSelect }: { templateInputToSelect: ModelToSelect | undefined }) => {
    this.state.set('templateInputToSelect', templateInputToSelect);
  };

  setColumnIdToBeUsedAsTemplateInput = ({
    columnIdToBeUsedAsTemplateInput,
  }: {
    columnIdToBeUsedAsTemplateInput: string | undefined;
  }) => {
    this.state.set('columnIdToBeUsedAsTemplateInput', columnIdToBeUsedAsTemplateInput);
  };

  setTemplateColumns = ({
    templateColumns,
  }: {
    templateColumns: TemplateTableApplicationsOutputListV1 | undefined;
  }) => {
    this.state.set('templateColumns', templateColumns);
  };

  setInputFeatureColumnIds = ({
    inputFeatureColumnIds,
  }: {
    inputFeatureColumnIds: Record<string, string[]> | undefined;
  }) => {
    this.state.set('inputFeatureColumnIds', inputFeatureColumnIds);
  };

  addRowItemIdsInsertedInGroupLevel = ({ ids }: { ids: string[] }) => {
    const allIds = ids.concat(this.rowItemIdsInsertedInGroupLevel);
    const uniqueIds = Array.from(new Set(allIds)).sort();
    if (!_.isEqual(uniqueIds, this.rowItemIdsInsertedInGroupLevel)) {
      this.state.set('rowItemIdsInsertedInGroupLevel', uniqueIds);
    }
  };

  clearRowItemIdsInsertedInGroupLevel = () => {
    this.state.set('rowItemIdsInsertedInGroupLevel', []);
  };

  setShowCreateTemplate = ({ show }: { show: boolean }) => {
    this.state.set('showCreateTemplate', show);
  };

  setIsEditingTemplate = ({ isEditingTemplate }: { isEditingTemplate: boolean }) => {
    this.state.set('isEditingTemplate', isEditingTemplate);
  };

  setEditTemplateRemovedOutputByColumnId = ({
    editTemplateRemovedOutputByColumnId,
  }: {
    editTemplateRemovedOutputByColumnId: Record<string, DerivedFeatureV1>;
  }) => {
    this.state.set('editTemplateRemovedOutputByColumnId', editTemplateRemovedOutputByColumnId);
  };

  setColumns = ({ columns }: { columns: ColumnDefinition[] }) => {
    this.state.set('columns', columns);
  };

  setHierarchiesUsingThisColumnAsGroup = ({ hierarchies }: { hierarchies: HierarchyOutputV1[] }) => {
    this.state.set('hierarchiesUsingThisColumnAsGroup', hierarchies);
  };

  setColumnToDelete = ({ column }: { column: ColumnDefinition | undefined }) => {
    this.state.set('columnToDelete', column);
  };

  setShowRetireTemplateModal = ({ show }: { show: boolean }) => {
    this.state.set('showRetireTemplateModal', show);
  };

  setStoredTableFailures = ({ storedTableFailures }: { storedTableFailures: StoredTableSearchFailures[] }) => {
    this.state.set('storedTableFailures', storedTableFailures);
  };

  setStoredTables = ({ storedTables }: { storedTables: ItemSearchPreviewV1[] }) => {
    this.state.set('storedTables', storedTables);
  };

  waitForAgGridApi = async (timeoutMs = 5000): Promise<GridApi> => {
    const agGridApi = this.agGridApi;
    if (agGridApi) {
      return agGridApi;
    }
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const checkApi = () => {
        const api = this.agGridApi;
        if (api) {
          resolve(api);
        } else if (Date.now() - start >= timeoutMs) {
          reject(new Error('Timed out waiting for agGridApi'));
        } else {
          setTimeout(checkApi, 50);
        }
      };
      checkApi();
    });
  };
}
